import React, { useState } from "react";
import { Brain } from "lucide-react";
import { useModel } from "../../../../context/ModelContext";
import { isVoiceModel } from "../../../../utils/NovaVoiceManager";
import { Model } from "../../../../types";

const ErrorAlert: React.FC<{ message: string }> = ({ message }) => (
  <div className="mb-4 p-2 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm sm:text-base">
    {message}
  </div>
);

const SuccessAlert: React.FC<{ message: string }> = ({ message }) => (
  <div className="mb-4 p-2 sm:p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm sm:text-base">
    {message}
  </div>
);

// Cost tiers rank models by how expensive they are to run. Virtual Assistants
// use this to auto-select the cheapest model that can handle their task, so an
// accurate tier here directly reduces spend.
const COST_TIERS: { value: number; label: string; hint: string }[] = [
  { value: 1, label: "$", hint: "Economy — cheapest, best for simple tasks" },
  { value: 2, label: "$$", hint: "Standard — balanced capability and cost" },
  { value: 3, label: "$$$", hint: "Premium — most capable, most expensive" },
];

const ViewModel: React.FC = () => {
  const {
    models,
    loading,
    error,
    toggleModelAvailability,
    isModelEnabled,
    setModelAsDefault,
    getDefaultModel,
    setModelAsVoiceDefault,
    setModelCostTier
  } = useModel();

  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const [defaultLoading, setDefaultLoading] = useState<string | null>(null);
  const [costTierLoading, setCostTierLoading] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSetCostTier = async (modelId: string, modelName: string, tier: number) => {
    try {
      setCostTierLoading(modelId);
      setLocalError(null);
      setSuccessMessage(null);

      await setModelCostTier(modelId, tier);
      const tierLabel = COST_TIERS.find(t => t.value === tier)?.hint ?? `tier ${tier}`;
      setSuccessMessage(`${modelName} cost tier set to ${tierLabel.split(" —")[0]}.`);

      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Failed to set cost tier");
    } finally {
      setCostTierLoading(null);
    }
  };

  const handleToggle = async (modelId: string) => {
    try {
      setToggleLoading(modelId);
      setLocalError(null);
      await toggleModelAvailability(modelId);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Failed to update model status");
    } finally {
      setToggleLoading(null);
    }
  };

  // Handle setting default model (voice models get the separate voice default)
  const handleSetDefault = async (model: Model) => {
    const modelId = model.foundation_model_id;
    const modelName = model.display_name;
    try {
      setDefaultLoading(modelId);
      setLocalError(null);
      setSuccessMessage(null);

      if (isVoiceModel(model)) {
        await setModelAsVoiceDefault(modelId);
        setSuccessMessage(`${modelName} has been set as the default voice model for all users.`);
      } else {
        await setModelAsDefault(modelId);
        setSuccessMessage(`${modelName} has been set as the default model for all users.`);
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Failed to set default model");
    } finally {
      setDefaultLoading(null);
    }
  };

  if (loading && !toggleLoading) {
    return (
      <div className="p-2 sm:p-4">
        <div className="animate-pulse space-y-2 sm:space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-16 sm:h-20 bg-gray-100 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const currentDefaultModel = getDefaultModel();

  return (
    <div className="p-2 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
        <h2 className="text-base sm:text-lg font-semibold mb-2 sm:mb-0">Model Management</h2>
        {currentDefaultModel && (
          <div className="text-xs sm:text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
            Default: {currentDefaultModel.display_name}
          </div>
        )}
      </div>
      
      {error && <ErrorAlert message={error} />}
      {localError && <ErrorAlert message={localError} />}
      {successMessage && <SuccessAlert message={successMessage} />}

      <div className="space-y-2 sm:space-y-4">
        {models.map(model => {
          // Voice models get "Set Voice Default"; everything else the normal chat default
          const isVoice = isVoiceModel(model);
          const isCurrentDefault = isVoice ? !!model.default_voice : model.default;
          const defaultLabel = isVoice ? 'Voice Default' : 'Default';
          return (
          <div
            key={model.foundation_model_id}
            className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-4 rounded-lg border hover:bg-gray-50 ${
              isCurrentDefault
                ? 'border-blue-300 bg-blue-50'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mt-1 sm:mt-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <h4 className="font-medium text-sm sm:text-base break-words">
                    {model.display_name}
                  </h4>
                  <div className="flex gap-1">
                    <span className={`text-xs sm:text-sm px-2 py-0.5 rounded-full inline-flex items-center w-fit ${
                      model.is_enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {isCurrentDefault && (
                      <span className="text-xs sm:text-sm px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 inline-flex items-center w-fit">
                        {defaultLabel}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  <span className="font-semibold text-gray-600">Provider:</span> {model.provider}
                </p>
                <div className="text-xs sm:text-sm text-gray-500 mt-1 break-words">
                  {model.short_description}
                </div>
              </div>
            </div>
            
            {/* Controls section */}
            <div className="flex items-center gap-2 sm:gap-3 mt-2 sm:mt-0 self-end sm:self-auto">
              {/* Cost tier — feeds automatic model selection for VAs */}
              <div className="flex items-center gap-1" title="Relative running cost. Virtual Assistants automatically pick the cheapest tier that can handle their task.">
                {costTierLoading === model.foundation_model_id ? (
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  COST_TIERS.map(tier => {
                    const isActive = (model.cost_tier ?? 2) === tier.value;
                    return (
                      <button
                        key={tier.value}
                        onClick={() => handleSetCostTier(model.foundation_model_id, model.display_name, tier.value)}
                        disabled={isActive}
                        title={tier.hint}
                        className={`
                          px-1.5 sm:px-2 py-1 text-xs font-medium rounded-md border transition-colors
                          ${isActive
                            ? 'bg-amber-100 text-amber-800 border-amber-300 cursor-default'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50 cursor-pointer'
                          }
                        `}
                      >
                        {tier.label}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Default Button */}
              <button
                onClick={() => handleSetDefault(model)}
                disabled={isCurrentDefault || !model.is_enabled || defaultLoading === model.foundation_model_id}
                className={`
                  px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-colors
                  ${isCurrentDefault
                    ? 'bg-blue-100 text-blue-800 cursor-default'
                    : !model.is_enabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 cursor-pointer'
                  }
                  ${defaultLoading === model.foundation_model_id ? 'opacity-50' : ''}
                `}
                title={
                  isCurrentDefault
                    ? `This is the current ${isVoice ? 'default voice model' : 'default model'}`
                    : !model.is_enabled
                    ? 'Model must be enabled to set as default'
                    : isVoice
                    ? 'Set as the model voice mode uses for all users'
                    : 'Set as default model for all users'
                }
              >
                {defaultLoading === model.foundation_model_id ? (
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  isCurrentDefault ? defaultLabel : `Set ${defaultLabel}`
                )}
              </button>

              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-end sm:justify-center relative w-16 sm:w-11">
                {toggleLoading === model.foundation_model_id ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={isModelEnabled(model.foundation_model_id)}
                      onChange={() => handleToggle(model.foundation_model_id)}
                    />
                    <div className={`
                      w-9 sm:w-11 h-5 sm:h-6 bg-gray-200 
                      peer-focus:outline-none peer-focus:ring-4 
                      peer-focus:ring-purple-300 rounded-full peer 
                      peer-checked:after:translate-x-full 
                      peer-checked:after:border-white 
                      after:content-[''] after:absolute 
                      after:top-[2px] after:left-[2px] 
                      after:bg-white after:border-gray-300 
                      after:border after:rounded-full 
                      after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 
                      after:transition-all 
                      peer-checked:bg-purple-600
                    `}></div>
                  </label>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default ViewModel;